/**
 * Offer package policy helpers (booking window, payment methods, cancel/reschedule flags).
 *
 * Admin (source of truth) columns on hero_offers:
 *   allowed_payment_methods text[]  — e.g. ['cash'], ['card'], ['cash','card']
 *   reschedule_allowed, cancel_allowed
 *   reschedule_policy_override, cancel_policy_override
 *
 * Legacy / migration aliases still supported:
 *   payment_methods ('cash'|'card'|'both')
 *   allow_reschedule, allow_cancel, override_*_policy
 */

const toDateOnly = (value) => {
  if (!value) return null
  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return new Date(value.getFullYear(), value.getMonth(), value.getDate())
  }
  const s = String(value).trim()
  if (!s) return null
  const m = s.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]))
  const d = new Date(s)
  if (Number.isNaN(d.getTime())) return null
  return new Date(d.getFullYear(), d.getMonth(), d.getDate())
}

const startOfToday = () => {
  const n = new Date()
  return new Date(n.getFullYear(), n.getMonth(), n.getDate())
}

const addDays = (date, days) => {
  const d = new Date(date.getFullYear(), date.getMonth(), date.getDate())
  d.setDate(d.getDate() + days)
  return d
}

const formatYmd = (date) => {
  if (!date) return ''
  const y = date.getFullYear()
  const m = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

const pickFirstDefined = (...values) => {
  for (const v of values) {
    if (v !== undefined && v !== null) return v
  }
  return undefined
}

const resolveBool = (values, defaultValue) => {
  const v = pickFirstDefined(...values)
  if (v === undefined) return defaultValue
  return v !== false && v !== 'false' && v !== 0 && v !== '0'
}

const resolveTrueFlag = (values) => {
  const v = pickFirstDefined(...values)
  return v === true || v === 'true' || v === 1 || v === '1'
}

/** Normalize payment_methods from DB / deal (array or string). */
export function normalizeOfferPaymentMethods(raw) {
  if (Array.isArray(raw)) {
    const set = new Set(
      raw
        .map((x) => String(x || '').toLowerCase().trim())
        .filter(Boolean)
    )
    const hasCash = set.has('cash')
    const hasCard = set.has('card')
    if (hasCash && hasCard) return 'both'
    if (hasCash) return 'cash'
    if (hasCard) return 'card'
    return 'both'
  }

  if (raw && typeof raw === 'object') {
    const hasCash = !!(raw.cash || raw.Cash)
    const hasCard = !!(raw.card || raw.Card)
    if (hasCash && hasCard) return 'both'
    if (hasCash) return 'cash'
    if (hasCard) return 'card'
  }

  const v = String(raw || 'both').toLowerCase().trim()
  if (v === 'cash' || v === 'card' || v === 'both') return v
  if (v.includes('cash') && v.includes('card')) return 'both'
  if (v.includes('cash')) return 'cash'
  if (v.includes('card')) return 'card'
  return 'both'
}

/** Resolve payment mode from admin + legacy fields on an offer/deal. */
export function resolveOfferPaymentMethodsRaw(offerOrDeal) {
  const src = offerOrDeal || {}
  return pickFirstDefined(
    src.allowed_payment_methods,
    src.allowedPaymentMethods,
    // Only use legacy payment_methods when it is not the unused default "both"
    // while admin array is missing — still prefer explicit cash/card on legacy.
    src.payment_methods,
    src.paymentMethods,
    'both'
  )
}

export function getAllowedPaymentMethodsForOffer(offerOrDeal) {
  // Prefer admin array when present (even if legacy payment_methods is still "both")
  const src = offerOrDeal || {}
  const adminRaw = pickFirstDefined(src.allowed_payment_methods, src.allowedPaymentMethods)
  const raw =
    adminRaw !== undefined
      ? adminRaw
      : pickFirstDefined(src.payment_methods, src.paymentMethods, 'both')
  const mode = normalizeOfferPaymentMethods(raw)
  if (mode === 'cash') return ['cash']
  if (mode === 'card') return ['card']
  return ['cash', 'card']
}

/**
 * Bookable calendar bounds for an offer:
 * min = max(today, starts_at), max = ends_at (or today+30 if missing, capped 31 days from min).
 */
export function getOfferBookingWindow(offerOrDeal, { today = startOfToday() } = {}) {
  if (!offerOrDeal) return null

  // Services split across days (open-hours overflow) are not a package/offer.
  // Do not invent a 30-day offer validity window for them.
  const isDurationSplit =
    offerOrDeal.isDurationSplit === true || offerOrDeal.isPackageOffer === false
  const starts = toDateOnly(offerOrDeal.starts_at || offerOrDeal.startsAt || offerOrDeal.windowStart)
  const ends = toDateOnly(offerOrDeal.ends_at || offerOrDeal.endsAt || offerOrDeal.windowEnd)
  if (isDurationSplit && !starts && !ends) {
    return null
  }

  const visits = Math.max(1, Number(offerOrDeal.total_days || offerOrDeal.totalDays) || 1)

  let minDate = today
  if (starts && starts > minDate) minDate = starts

  let maxDate = ends
  if (!maxDate) {
    maxDate = addDays(minDate, 30)
  }

  const hardMax = addDays(minDate, 30)
  if (maxDate > hardMax) maxDate = hardMax

  if (maxDate < minDate) return null

  const spanDays = Math.floor((maxDate - minDate) / (24 * 60 * 60 * 1000)) + 1
  if (spanDays < visits) {
    return {
      minDate,
      maxDate,
      minKey: formatYmd(minDate),
      maxKey: formatYmd(maxDate),
      visits,
      spanDays,
      valid: false,
      message: `This package needs ${visits} visit days, but the offer window only has ${spanDays} calendar day(s).`,
    }
  }

  return {
    minDate,
    maxDate,
    minKey: formatYmd(minDate),
    maxKey: formatYmd(maxDate),
    visits,
    spanDays,
    valid: true,
    message: null,
  }
}

export function isDateInsideOfferWindow(dateValue, offerOrDeal) {
  const window = getOfferBookingWindow(offerOrDeal)
  if (!window?.valid) return false
  const d = toDateOnly(dateValue)
  if (!d) return false
  return d >= window.minDate && d <= window.maxDate
}

export function getOfferPolicyFlags(offerOrDeal) {
  const src = offerOrDeal || {}
  return {
    allowReschedule: resolveBool(
      [src.reschedule_allowed, src.rescheduleAllowed, src.allow_reschedule, src.allowReschedule],
      true
    ),
    allowCancel: resolveBool(
      [src.cancel_allowed, src.cancelAllowed, src.allow_cancel, src.allowCancel],
      true
    ),
    overrideReschedulePolicy: resolveTrueFlag([
      src.reschedule_policy_override,
      src.reschedulePolicyOverride,
      src.override_reschedule_policy,
      src.overrideReschedulePolicy,
    ]),
    overrideCancelPolicy: resolveTrueFlag([
      src.cancel_policy_override,
      src.cancelPolicyOverride,
      src.override_cancel_policy,
      src.overrideCancelPolicy,
    ]),
  }
}

/**
 * Fields to attach onto a booking `deal` object from a hero offer row.
 */
export function buildOfferPolicyFields(offer) {
  if (!offer) return {}
  const flags = getOfferPolicyFlags(offer)
  const paymentMode = normalizeOfferPaymentMethods(
    pickFirstDefined(
      offer.allowed_payment_methods,
      offer.allowedPaymentMethods,
      offer.payment_methods,
      offer.paymentMethods,
      'both'
    )
  )
  const adminMethods = pickFirstDefined(offer.allowed_payment_methods, offer.allowedPaymentMethods)
  return {
    starts_at: offer.starts_at || offer.startsAt || null,
    ends_at: offer.ends_at || offer.endsAt || null,
    payment_methods: paymentMode,
    allowed_payment_methods: Array.isArray(adminMethods)
      ? adminMethods
      : paymentMode === 'both'
        ? ['cash', 'card']
        : [paymentMode],
    allow_reschedule: flags.allowReschedule,
    allow_cancel: flags.allowCancel,
    override_reschedule_policy: flags.overrideReschedulePolicy,
    override_cancel_policy: flags.overrideCancelPolicy,
    reschedule_allowed: flags.allowReschedule,
    cancel_allowed: flags.allowCancel,
    reschedule_policy_override: flags.overrideReschedulePolicy,
    cancel_policy_override: flags.overrideCancelPolicy,
  }
}
